import type { Redirect } from '../types/seo'

/**
 * Doorverwijzingen (301) van oude adressen naar nieuwe. Puur: het beheer waarschuwt ermee, de
 * server weigert dezelfde fouten (lus, dubbel, ongeldig pad) met 422.
 */

export type RedirectIssue = { level: 'error' | 'warning'; text: string }

/** Ketens langer dan dit zijn bijna altijd een vergissing (en Google volgt ze slecht). */
const MAX_HOPS = 5

/**
 * "https://beautybewust.nl/Product/Serum/?x=1" → "/product/serum". Een pad zonder slash vooraan
 * krijgt er een. Kleine letters, geen afsluitende slash (behalve "/"), query blijft weg.
 * `null` als het geen pad op deze site is.
 */
export function normalizePath(input: string, siteHost = 'beautybewust.nl'): string | null {
  let s = input.trim()
  if (!s) return null
  const abs = s.match(/^https?:\/\/([^/]+)(\/.*)?$/i)
  if (abs) {
    const host = abs[1].toLowerCase().replace(/^www\./, '')
    if (host !== siteHost) return null
    s = abs[2] ?? '/'
  }
  if (/^[a-z]+:/i.test(s) || s.startsWith('//')) return null
  if (!s.startsWith('/')) s = `/${s}`
  s = s.split('#')[0].split('?')[0].toLowerCase().replace(/\/{2,}/g, '/')
  if (s.length > 1) s = s.replace(/\/+$/, '')
  return /^\/[a-z0-9\-._~/%]*$/.test(s) ? s : null
}

/** Doel: een pad op deze site (genormaliseerd) of een volledige https-link elders. */
export function normalizeTarget(input: string, siteHost = 'beautybewust.nl'): string | null {
  const s = input.trim()
  if (/^https:\/\//i.test(s)) {
    const own = normalizePath(s, siteHost)
    return own ?? (/^https:\/\/[^\s/]+\.[^\s/]+/i.test(s) ? s : null)
  }
  return normalizePath(s, siteHost)
}

/** Waar komt een pad na alle doorverwijzingen uit? `loop: true` als het rondjes draait. */
export function follow(path: string, items: Redirect[]): { end: string; hops: number; loop: boolean } {
  const map = new Map(items.map((r) => [r.from, r.to]))
  const seen = new Set<string>([path])
  let at = path
  let hops = 0
  while (map.has(at)) {
    const next = map.get(at) ?? at
    hops++
    if (seen.has(next) || hops > MAX_HOPS * 4) return { end: next, hops, loop: true }
    seen.add(next)
    at = next
  }
  return { end: at, hops, loop: false }
}

/**
 * Problemen per regel (zelfde volgorde als `items`). `exists(path)` zegt of een pad op de site
 * bestaat (vaste pagina, product, artikel, juridische pagina).
 */
export function redirectIssues(items: Redirect[], exists: (path: string) => boolean): RedirectIssue[][] {
  const counts = new Map<string, number>()
  items.forEach((r) => counts.set(r.from, (counts.get(r.from) ?? 0) + 1))

  return items.map((r) => {
    const out: RedirectIssue[] = []
    if (!normalizePath(r.from)) out.push({ level: 'error', text: 'Het oude adres is geen geldig pad' })
    const to = normalizeTarget(r.to)
    if (!to) out.push({ level: 'error', text: 'Het nieuwe adres is geen pad op deze site of https-link' })
    if (r.from === r.to) out.push({ level: 'error', text: 'Verwijst naar zichzelf' })
    if ((counts.get(r.from) ?? 0) > 1) out.push({ level: 'error', text: 'Dit oude adres staat er vaker in' })
    if (exists(r.from)) out.push({ level: 'warning', text: 'Dit adres bestaat op de nieuwe site: de doorverwijzing verbergt die pagina' })
    if (out.some((x) => x.level === 'error')) return out

    const route = follow(r.from, items)
    if (route.loop) out.push({ level: 'error', text: 'Lus: de doorverwijzingen komen weer bij elkaar uit' })
    else {
      if (route.hops > 1) out.push({ level: 'warning', text: `Keten van ${route.hops} stappen: verwijs liever direct naar ${route.end}` })
      if (route.end.startsWith('/') && !exists(route.end)) out.push({ level: 'warning', text: `${route.end} bestaat niet (meer) op de site` })
    }
    return out
  })
}

/**
 * CSV importeren: twee kolommen (oud, nieuw), gescheiden door komma of puntkomma. Een kopregel
 * ("van;naar", "from,to") en lege regels worden overgeslagen. Wat niet klopt, komt in `rejected`.
 */
export function parseRedirectsCsv(text: string, createdAt: string): { items: Redirect[]; rejected: { line: number; text: string }[] } {
  const items: Redirect[] = []
  const rejected: { line: number; text: string }[] = []
  text.split(/\r?\n/).forEach((raw, i) => {
    const line = raw.trim()
    if (!line) return
    const [a = '', b = ''] = line.split(/[;,\t]/).map((c) => c.trim().replace(/^"|"$/g, ''))
    if (i === 0 && /^(van|oud|from|old|source)$/i.test(a)) return
    const from = normalizePath(a)
    const to = normalizeTarget(b)
    if (!from || !to) rejected.push({ line: i + 1, text: line })
    else items.push({ from, to, createdAt })
  })
  return { items, rejected }
}

/** Nieuwe regels erbij; een bestaand oud adres wordt bijgewerkt (de laatste wint). */
export function mergeRedirects(current: Redirect[], incoming: Redirect[]): Redirect[] {
  const byFrom = new Map(current.map((r) => [r.from, r]))
  incoming.forEach((r) => byFrom.set(r.from, r))
  return [...byFrom.values()]
}

/**
 * Voorstel uit de oude WooCommerce-winkel: elk product met een `wooId` stond daar op
 * `/product/{slug}`, met dezelfde slug als het id nu (zie docs/server/woocommerce-mapping.ts).
 * Alleen wat er nog niet in staat.
 */
export function wooSuggestions(products: { id: string; wooId?: number }[], current: Redirect[], createdAt: string): Redirect[] {
  const taken = new Set(current.map((r) => r.from))
  return products
    .filter((p) => p.wooId !== undefined)
    .map((p) => ({ from: normalizePath(`/product/${p.id}`), to: `/webshop/${p.id}` }))
    .filter((r): r is { from: string; to: string } => r.from !== null && !taken.has(r.from))
    .map((r) => ({ ...r, createdAt }))
}

/** De vaste pagina's van de webshop (zie App.tsx), voor de waarschuwing "bestaat niet". */
const STATIC_PATHS = ['/', '/webshop', '/routines', '/behandelingen', '/journal', '/over-ons', '/contact', '/huidadvies', '/account', '/privacy', '/algemene-voorwaarden', '/verzending-en-retour']

/** Alle paden die nu op de site bestaan. Gedeeld door het beheer en de (demo)server. */
export function knownSitePaths(catalog: { products: { id: string }[]; articles: { slug: string }[] }): Set<string> {
  return new Set([...STATIC_PATHS, ...catalog.products.map((p) => `/webshop/${p.id}`), ...catalog.articles.map((a) => `/journal/${a.slug}`)])
}

/**
 * Voor de CSV-export: dezelfde vorm als de import. Elke regel gaat opnieuw door de controle van de
 * import, zodat er nooit iets in komt wat Excel als formule leest (`=HYPERLINK(…)`) of wat de
 * kolommen verschuift (`;`), ook niet als een regel ooit buiten de normale invoer om ontstond.
 * Geen `toCsv`: aanhalingstekens zouden de import (die op `;` splitst) breken.
 */
export const redirectsCsv = (items: Redirect[]) =>
  [
    'van;naar',
    ...items.flatMap((r) => {
      const from = normalizePath(r.from)
      const to = normalizeTarget(r.to)
      return from && to && !/[;\r\n"]/.test(from + to) ? [`${from};${to}`] : []
    }),
  ].join('\n')
